// 등·하원 키오스크 — 센터 공용 태블릿용 전체화면 (Header/TabBar 없음).
// 학생이 전화번호 뒷 4자리 입력 → 본인 이름 확인 → 등원/하원.
// 지각·조퇴 판정은 서버 RPC가 DB 시각으로 한다. 여기서는 입력·표시만.
// 같은 화면에서 교직원(admin/manager/instructor/consultant) 출퇴근도 함께 받는다
// (kiosk_find_staff 등 — scripts/add-staff-attendance.sql). 교직원 조회는 마이그레이션
// 미적용이어도 학생 흐름이 깨지지 않도록 항상 .catch(() => [])로 감싼다.

import { useState, useEffect, useRef, useCallback } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { Delete, X, LogIn, LogOut, CheckCircle2, AlertCircle, Loader } from 'lucide-react'
import { useData } from '../../context/DataContext.jsx'

const RESET_DELAY_MS = 3000

const STAFF_ROLE_LABEL = {
  admin: '센터장',
  manager: '매니저',
  instructor: '강사',
  consultant: '컨설턴트',
}

// RPC 결과 → 결과 화면 문구
function checkInMessage(student, { result, corrected, noSchedule, reentry }) {
  if (result === 'already_in') return { tone: 'info', text: `${student.name}님은 이미 등원 처리되어 있어요` }
  if (reentry) return { tone: 'ok', text: `${student.name}님 재등원 완료` }
  if (corrected) return { tone: 'warn', text: `${student.name}님 등원 완료 · 지각 (자동결석 정정)` }
  if (result === 'late') return { tone: 'warn', text: `${student.name}님 등원 완료 · 지각` }
  if (noSchedule) return { tone: 'ok', text: `${student.name}님 등원 완료 (오늘 등원 예정 없음)` }
  return { tone: 'ok', text: `${student.name}님 등원 완료` }
}

function checkOutMessage(student, { result }) {
  if (result === 'no_check_in') return { tone: 'error', text: '등원 기록이 없어 하원 처리할 수 없어요' }
  if (result === 'early_leave') return { tone: 'warn', text: `${student.name}님 하원 완료 · 조퇴 처리` }
  return { tone: 'ok', text: `${student.name}님 하원 완료` }
}

// 교직원 출퇴근 결과 문구
function staffCheckInMessage(staff, { result, reentry }) {
  if (result === 'already_in') return { tone: 'info', text: `${staff.name}님은 이미 출근 상태입니다` }
  if (reentry) return { tone: 'ok', text: `${staff.name}님 재출근이 기록되었습니다` }
  return { tone: 'ok', text: `${staff.name}님 출근이 기록되었습니다` }
}

function staffCheckOutMessage(staff, { result }) {
  if (result === 'no_check_in') return { tone: 'error', text: '출근 기록이 없어 퇴근 처리할 수 없습니다' }
  return { tone: 'ok', text: `${staff.name}님 퇴근이 기록되었습니다` }
}

const TONE_STYLES = {
  ok: 'bg-emerald-50 text-emerald-700',
  warn: 'bg-yellow-50 text-yellow-700',
  info: 'bg-blue-50 text-blue-700',
  error: 'bg-red-50 text-red-700',
}

export default function KioskPage() {
  const navigate = useNavigate()
  const { pathname } = useLocation()
  const {
    kioskFindStudents, kioskCheckIn, kioskCheckOut,
    kioskFindStaff, kioskStaffCheckIn, kioskStaffCheckOut,
  } = useData()
  // /admin·/manager·/instructor·/consultant/kiosk 어디에나 마운트된다 — 종료 시 각자 첫 화면으로.
  const exitTo =
    pathname.startsWith('/admin') ? '/admin/home'
    : pathname.startsWith('/manager') ? '/manager/attendance'
    : `/${pathname.split('/')[1]}/students` // 강사·컨설턴트: 학생 탭으로

  // step: input → select → result
  const [digits, setDigits] = useState('')
  const [step, setStep] = useState('input')
  const [candidates, setCandidates] = useState([])
  const [staffCandidates, setStaffCandidates] = useState([])
  const [message, setMessage] = useState(null) // { tone, text }
  const [busy, setBusy] = useState(false)
  const resetTimer = useRef(null)

  const reset = useCallback(() => {
    clearTimeout(resetTimer.current)
    setDigits('')
    setStep('input')
    setCandidates([])
    setStaffCandidates([])
    setMessage(null)
    setBusy(false)
  }, [])

  useEffect(() => () => clearTimeout(resetTimer.current), [])

  const showResult = (msg) => {
    setMessage(msg)
    setStep('result')
    clearTimeout(resetTimer.current)
    resetTimer.current = setTimeout(reset, RESET_DELAY_MS)
  }

  const search = async (fourDigits) => {
    setBusy(true)
    try {
      // 교직원 조회는 마이그레이션 미적용 시에도 학생 흐름이 절대 깨지지 않게 감싼다.
      const [found, foundStaff] = await Promise.all([
        kioskFindStudents(fourDigits),
        kioskFindStaff(fourDigits).catch(() => []),
      ])
      if (found.length === 0 && foundStaff.length === 0) {
        showResult({ tone: 'error', text: '해당 번호의 학생이 없어요. 다시 확인해 주세요.' })
      } else {
        setCandidates(found)
        setStaffCandidates(foundStaff)
        setStep('select')
      }
    } catch {
      showResult({ tone: 'error', text: '조회에 실패했어요. 잠시 후 다시 시도해 주세요.' })
    } finally {
      setBusy(false)
    }
  }

  const pressDigit = (d) => {
    if (busy || step !== 'input') return
    const next = (digits + d).slice(0, 4)
    setDigits(next)
    if (next.length === 4) search(next)
  }

  const handleCheckIn = async (student) => {
    if (busy) return
    setBusy(true)
    try {
      const res = await kioskCheckIn(student.id)
      showResult(checkInMessage(student, res))
    } catch {
      showResult({ tone: 'error', text: '처리에 실패했어요. 다시 시도해 주세요.' })
    } finally {
      setBusy(false)
    }
  }

  const handleCheckOut = async (student) => {
    if (busy) return
    setBusy(true)
    try {
      const res = await kioskCheckOut(student.id)
      showResult(checkOutMessage(student, res))
    } catch {
      showResult({ tone: 'error', text: '처리에 실패했어요. 다시 시도해 주세요.' })
    } finally {
      setBusy(false)
    }
  }

  const handleStaffCheckIn = async (staff) => {
    if (busy) return
    setBusy(true)
    try {
      const res = await kioskStaffCheckIn(staff.id)
      showResult(staffCheckInMessage(staff, res))
    } catch {
      showResult({ tone: 'error', text: '처리에 실패했어요. 다시 시도해 주세요.' })
    } finally {
      setBusy(false)
    }
  }

  const handleStaffCheckOut = async (staff) => {
    if (busy) return
    setBusy(true)
    try {
      const res = await kioskStaffCheckOut(staff.id)
      showResult(staffCheckOutMessage(staff, res))
    } catch {
      showResult({ tone: 'error', text: '처리에 실패했어요. 다시 시도해 주세요.' })
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center px-6 py-8 select-none">
      {/* 교직원용 나가기 — 학생 눈에 안 띄게 작게 */}
      <button
        onClick={() => navigate(exitTo)}
        className="fixed top-3 right-3 text-gray-300 hover:text-gray-500 p-2"
        aria-label="키오스크 종료"
      >
        <X size={20} />
      </button>

      <div className="w-full max-w-md space-y-6">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900">등·하원 체크</h1>
          <p className="text-sm text-gray-500 mt-1">전화번호 뒷 4자리를 눌러 주세요</p>
        </div>

        {/* 입력 표시 */}
        <div className="flex justify-center gap-3">
          {[0, 1, 2, 3].map((i) => (
            <div
              key={i}
              className={`w-14 h-16 rounded-2xl border-2 flex items-center justify-center text-3xl font-bold ${
                digits[i] ? 'border-indigo-400 bg-white text-gray-900' : 'border-gray-200 bg-white text-gray-300'
              }`}
            >
              {digits[i] ?? ''}
            </div>
          ))}
        </div>

        {step === 'input' && (
          <div className="grid grid-cols-3 gap-3">
            {['1', '2', '3', '4', '5', '6', '7', '8', '9'].map((d) => (
              <button
                key={d}
                onClick={() => pressDigit(d)}
                className="h-20 bg-white rounded-2xl shadow-sm text-3xl font-bold text-gray-800 active:scale-95 active:bg-indigo-50 transition-all"
              >
                {d}
              </button>
            ))}
            <button
              onClick={reset}
              className="h-20 bg-white rounded-2xl shadow-sm text-base font-semibold text-gray-400 active:scale-95 transition-all"
            >
              전체 지움
            </button>
            <button
              onClick={() => pressDigit('0')}
              className="h-20 bg-white rounded-2xl shadow-sm text-3xl font-bold text-gray-800 active:scale-95 active:bg-indigo-50 transition-all"
            >
              0
            </button>
            <button
              onClick={() => setDigits(digits.slice(0, -1))}
              className="h-20 bg-white rounded-2xl shadow-sm flex items-center justify-center text-gray-400 active:scale-95 transition-all"
              aria-label="한 글자 지움"
            >
              <Delete size={28} />
            </button>
          </div>
        )}

        {busy && step === 'input' && (
          <div className="flex justify-center text-gray-400">
            <Loader size={24} className="animate-spin" />
          </div>
        )}

        {/* 본인 확인 + 등/하원 선택 */}
        {step === 'select' && (
          <div className="space-y-3">
            <p className="text-center text-sm font-semibold text-gray-600">
              본인 이름을 확인하고 눌러 주세요
            </p>
            {candidates.map((s) => (
              <div key={s.id} className="bg-white rounded-2xl p-4 shadow-sm space-y-3">
                <div className="text-center">
                  <p className="text-xl font-bold text-gray-900">{s.name}</p>
                  <p className="text-xs text-gray-400 mt-0.5">
                    {s.grade}{s.className ? ` · ${s.className}` : ''}
                  </p>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => handleCheckIn(s)}
                    disabled={busy || s.checkedIn}
                    className="flex-1 h-16 rounded-xl bg-indigo-500 text-white text-lg font-bold flex items-center justify-center gap-2 active:scale-95 transition-all disabled:opacity-30 disabled:cursor-not-allowed"
                  >
                    <LogIn size={22} /> {s.checkedOut ? '재등원' : '등원'}
                  </button>
                  <button
                    onClick={() => handleCheckOut(s)}
                    disabled={busy || !s.checkedIn}
                    className="flex-1 h-16 rounded-xl bg-emerald-500 text-white text-lg font-bold flex items-center justify-center gap-2 active:scale-95 transition-all disabled:opacity-30 disabled:cursor-not-allowed"
                  >
                    <LogOut size={22} /> 하원
                  </button>
                </div>
                {s.checkedIn && !s.checkedOut && (
                  <p className="text-center text-[11px] text-gray-400">등원 완료 상태예요</p>
                )}
                {s.checkedOut && (
                  <p className="text-center text-[11px] text-gray-400">
                    하원 완료 상태예요 · 다시 등원하려면 재등원을 눌러 주세요
                  </p>
                )}
              </div>
            ))}
            {staffCandidates.map((st) => (
              <div key={st.id} className="bg-white rounded-2xl p-4 shadow-sm space-y-3 border border-indigo-100">
                <div className="text-center">
                  <p className="text-xl font-bold text-gray-900">
                    {st.name}
                    <span className="ml-1.5 align-middle text-[11px] font-bold text-indigo-500 bg-indigo-50 rounded-full px-2 py-0.5">
                      {STAFF_ROLE_LABEL[st.role] ?? '교직원'}
                    </span>
                  </p>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => handleStaffCheckIn(st)}
                    disabled={busy || st.checkedIn}
                    className="flex-1 h-16 rounded-xl bg-indigo-500 text-white text-lg font-bold flex items-center justify-center gap-2 active:scale-95 transition-all disabled:opacity-30 disabled:cursor-not-allowed"
                  >
                    <LogIn size={22} /> 출근
                  </button>
                  <button
                    onClick={() => handleStaffCheckOut(st)}
                    disabled={busy || !st.checkedIn}
                    className="flex-1 h-16 rounded-xl bg-emerald-500 text-white text-lg font-bold flex items-center justify-center gap-2 active:scale-95 transition-all disabled:opacity-30 disabled:cursor-not-allowed"
                  >
                    <LogOut size={22} /> 퇴근
                  </button>
                </div>
                {st.checkedIn && !st.checkedOut && (
                  <p className="text-center text-[11px] text-gray-400">출근 완료 상태예요</p>
                )}
                {st.checkedOut && (
                  <p className="text-center text-[11px] text-gray-400">
                    퇴근 완료 상태예요 · 다시 출근하려면 출근을 눌러 주세요
                  </p>
                )}
              </div>
            ))}
            <button
              onClick={reset}
              className="w-full py-4 text-gray-400 font-semibold text-sm"
            >
              처음으로
            </button>
          </div>
        )}

        {/* 결과 — 3초 후 자동 초기화 */}
        {step === 'result' && message && (
          <div className={`rounded-2xl p-6 text-center space-y-2 ${TONE_STYLES[message.tone]}`}>
            {message.tone === 'error'
              ? <AlertCircle size={40} className="mx-auto" />
              : <CheckCircle2 size={40} className="mx-auto" />}
            <p className="text-lg font-bold">{message.text}</p>
            <button onClick={reset} className="text-xs opacity-60 underline">바로 처음으로</button>
          </div>
        )}
      </div>
    </div>
  )
}
